import { ActivatedRoute, Router } from '@angular/router';
import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject, linkedSignal, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { OtapUpgradeService } from '@services/otap-upgrade/otap.upgrade.service';
import { OtapUpgradeGatewayDTO } from '@app/shared/models/otap-upgrade-gateway.inerface';
import { OtapUpgradeTaskDTO } from '@app/shared/models/otap-upgrade-task.interface';
import { OtapUpgradeDTO } from '@app/shared/models/otap.upgrade.interface';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { OtapWebsocketService } from '@services/otap-upgrade/otap-websocket-service';
import { GatewayHealthDTO, GatewayMetricDTO, JobNotification } from '@app/shared/models/gateway-health-dto.interface';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatCard } from '@angular/material/card';
import { GatewayDetailsComponent } from './gateway-details/gateway-details.component';
import { JobTasksComponent } from './job-tasks/job-tasks.component';
import { MatButton } from '@angular/material/button';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';

class OtapNotification {
  timestamp: string;
  gateway: string;
  message: string;
}

@Component({
  selector: 'app-job-information',
  templateUrl: './job-information.component.html',
  styleUrls: ['./job-information.component.scss'],
  imports: [
    MatTabGroup,
    MatTab,
    MatCard,
    GatewayDetailsComponent,
    JobTasksComponent,
    MatButton,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription
  ]
})
export class JobInformationComponent implements OnInit, OnDestroy {
  private readonly otapUpgradeService = inject(OtapUpgradeService);
  private readonly otapWebsocketService = inject(OtapWebsocketService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private gatewayDetailsSubscription?: { unsubscribe(): void };
  private readonly selectedJob = toSignal(this.otapUpgradeService.selectedJob$, { initialValue: null });

  readonly job = linkedSignal(() => this.selectedJob());
  readonly gateways = signal<OtapUpgradeGatewayDTO[]>([]);
  readonly tasks = signal<OtapUpgradeTaskDTO[]>([]);
  readonly jobStartEnabled = signal(true);
  readonly exportStatus = signal<{ exportId: string; complete: boolean } | null>(null);
  readonly exportCompleteEvent = output<{ exportId: string; complete: boolean }>();
  readonly otapNotifications = signal<OtapNotification[]>([]);
  private readonly jobRunnable = signal(false);
  private readonly job$ = toObservable(this.job);

  ngOnInit(): void {
    const init$ = this.route.paramMap.pipe(
      switchMap(params => {
        const id = params.get('jobId');
        if (!id) return of(null);
        // If already selected and matches, reuse
        return this.otapUpgradeService.selectedJob$.pipe(
          take(1),
          switchMap(cur => {
            if (cur && String(cur.id) === id) return of(cur);
            // Otherwise fetch and set
            return this.otapUpgradeService.getJob(Number(id)).pipe(
              tap(job => this.otapUpgradeService.updateSelectedJob(job)),
              catchError(() => {
                this.router.navigate(['/global-administration/otap-upgrades']);
                return of(null);
              })
            );
          })
        );
      })
    );

    init$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.job$
      .pipe(
        switchMap(job => {
          if (!job) return of<[OtapUpgradeGatewayDTO[], OtapUpgradeTaskDTO[]]>([[], []]);
          this.jobRunnable.set(job.status === 'CREATED' || job.status === 'STARTED');
          return combineLatest([
            this.otapUpgradeService.getAllGatewaysForAJob(job.id),
            this.otapUpgradeService.getAllTasksForAJob(job.id)
          ]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([gateways, tasks]) => {
        this.gateways.set(gateways);
        this.tasks.set(tasks);
        this.setStartButtonStatus(tasks);
      });

    if (!this.otapWebsocketService.isConnected()) {
      this.otapWebsocketService.startConnection();
    }

    this.initializeWebsocketListener();
  }

  ngOnDestroy(): void {
    this.gatewayDetailsSubscription?.unsubscribe();
    this.otapWebsocketService.disconnect();
  }

  private setStartButtonStatus(tasks: OtapUpgradeTaskDTO[]): void {
    let enableStartButtons = this.jobRunnable();
    if (tasks !== null) {
      for (const task of tasks) {
        if (task.statuses) {
          if (task.statuses.length > 0) {
            if (this.checkIfAnyTaskIsRunning(task.statuses)) {
              enableStartButtons = false;
              break;
            }
          }
        }
      }
    }
    this.jobStartEnabled.set(enableStartButtons);
  }

  onJobStartStateChanged(updatedJobStartState: boolean): void {
    this.jobStartEnabled.set(updatedJobStartState);
  }

  onOtapNotification(message: string): void {
    const json = JSON.parse(message);

    const otapNotification = new OtapNotification();
    otapNotification.timestamp = new Date(json.otapResponseNotification.receivedTime).toLocaleString();
    otapNotification.gateway = json.otapResponseNotification.gatewayAddress;
    let otapMessage = '';
    for (const x in json.otapResponseNotification) {
      if (x !== 'gatewayAddress' && x !== 'receivedTime') {
        otapMessage = x;
      }
    }
    otapNotification.message =
      '{"' + otapMessage + '": ' + JSON.stringify(json.otapResponseNotification[otapMessage]) + '}';

    this.otapNotifications.update((notifications) => [otapNotification, ...notifications].slice(0, 1000));
  }

  clearOtapNotifications(): void {
    this.otapNotifications.set([]);
  }

  private initializeWebsocketListener(): void {
    this.job$
      .pipe(
        filter((job): job is OtapUpgradeDTO => Boolean(job?.id)),
        switchMap(job =>
          this.otapWebsocketService.listenToJob(job.id, (packet) => {
              const parsedData: JobNotification = JSON.parse(packet.body);
              switch (parsedData['notification-type']) {
                case 'gateway':
                  this.updateGatewayData(parsedData as unknown as { gatewayId: number; sensorNodeCount: number });
                  break;
                case 'gateway-health':
                  this.updateGatewayHealthOrMetricData(parsedData as GatewayHealthDTO, 'gatewayHealth');
                  break;
                case 'gateway-metric':
                  this.updateGatewayHealthOrMetricData(parsedData as GatewayMetricDTO, 'gatewayMetric');
                  break;
                case 'job-task':
                  this.updateTaskData(parsedData as unknown as OtapUpgradeTaskDTO);
                  break;
                case 'gateway-response':
                  // Ignore for now
                  break;
                case 'export-complete':
                  this.handleExportComplete(parsedData as unknown as { exportId: string });
                  break;
                case 'otap-response-notification':
                  this.onOtapNotification(packet.body);
                  break;
                default:
                  console.warn('Unknown notification type: ', parsedData);
              }
              this.changeDetectorRef.markForCheck();
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((subscription) => {
        this.gatewayDetailsSubscription?.unsubscribe();
        this.gatewayDetailsSubscription = subscription;
      });
  }

  private updateGatewayData(gatewayData: { gatewayId: number; sensorNodeCount: number }): void {
    this.gateways.update(gateways =>
      gateways.map(gateway =>
        gateway.id === gatewayData.gatewayId ? { ...gateway, sensorNodeCount: gatewayData.sensorNodeCount } : gateway
      )
    );
  }

  private updateGatewayHealthOrMetricData(
    gatewayData: GatewayHealthDTO | GatewayMetricDTO,
    notificationType: 'gatewayHealth' | 'gatewayMetric'
  ): void {
    this.gateways.update(gateways =>
      gateways.map(gateway =>
        gateway.id !== gatewayData.gatewayId
          ? gateway
          : notificationType === 'gatewayHealth'
            ? { ...gateway, gatewayHealth: gatewayData as GatewayHealthDTO }
            : { ...gateway, gatewayMetric: gatewayData as GatewayMetricDTO }
      )
    );
  }

  private updateTaskData(taskData: OtapUpgradeTaskDTO): void {
    this.tasks.update(tasks => tasks.map(task => (task.id === taskData.id ? taskData : task)));
    if (taskData.statuses) {
      this.checkTaskEndDate(taskData.statuses);
    }
  }

  checkTaskEndDate(taskStatuses: OtapUpgradeTaskDTO['statuses']): void {
    let allTasksEnded = true;
    for (const status of taskStatuses) {
      if (!status.endedDateTime || status.endedDateTime === null) {
        allTasksEnded = false;
        break;
      }
    }
    this.jobStartEnabled.set(allTasksEnded);
  }

  checkIfAnyTaskIsRunning(taskStatuses: OtapUpgradeTaskDTO['statuses']): boolean {
    return taskStatuses.some(status => !status.endedDateTime);
  }

  private handleExportComplete(notification: { exportId: string }): void {
    const exportId = notification.exportId;
    this.exportCompleteEvent.emit({ exportId, complete: true });
    this.exportStatus.set({ exportId, complete: true });
  }
}
